import { useEffect, useState } from 'react';

/** Delay before switching from the short “working” line to the gathering line. */
export const AGENT_REPLY_WAIT_EXTENDED_MS = 10_000;

export const AGENT_REPLY_WAIT_INITIAL =
  'Your guide is working…';

export const AGENT_REPLY_WAIT_EXTENDED =
  'Still working — gathering context and preparing a reply…';

export type AgentReplyWaitCopy = {
  /** True while `active` — show the wait indicator. */
  visible: boolean;
  /** Current status line (initial or extended). */
  message: string;
  /** True after {@link AGENT_REPLY_WAIT_EXTENDED_MS} while still active. */
  extended: boolean;
};

/**
 * Phased wait copy while an agent chat turn is in flight.
 * Initial reassurance, then a longer “gathering context” line after ~10s.
 */
export function usePhasedAgentReplyWait(
  active: boolean,
  options?: {
    initialMessage?: string;
    extendedMessage?: string;
    extendedAfterMs?: number;
  }
): AgentReplyWaitCopy {
  const initialMessage = options?.initialMessage ?? AGENT_REPLY_WAIT_INITIAL;
  const extendedMessage = options?.extendedMessage ?? AGENT_REPLY_WAIT_EXTENDED;
  const extendedAfterMs = options?.extendedAfterMs ?? AGENT_REPLY_WAIT_EXTENDED_MS;

  const [extended, setExtended] = useState(false);

  useEffect(() => {
    if (!active) {
      setExtended(false);
      return;
    }
    setExtended(false);
    const timer = window.setTimeout(() => setExtended(true), extendedAfterMs);
    return () => window.clearTimeout(timer);
  }, [active, extendedAfterMs]);

  if (!active) {
    return { visible: false, message: initialMessage, extended: false };
  }

  return {
    visible: true,
    message: extended ? extendedMessage : initialMessage,
    extended,
  };
}
